// frontend/src/pages/LinhaTempo.js - VERSÃO ATUALIZADA E INTEGRADA

import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  CircularProgress, Paper, Stepper, Step, StepLabel, StepContent, Chip,
  Grid, Divider, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  List, ListItem, ListItemText, ListItemIcon, Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon, Timeline as TimelineIcon, Business as BusinessIcon,
  Schedule as ScheduleIcon, LocalShipping as ShippingIcon, Store as StoreIcon,
  Info as InfoIcon, Refresh as RefreshIcon, QrCodeScanner as ScannerIcon,
  Science as LabIcon, Error as ErrorIcon, CheckCircle as CheckCircleIcon,
  ReportProblem as DivergenciaIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Formata data/hora para exibição no fuso do navegador (evita mostrar ISO bruto)
const formatarDataHoraLocal = (valor) => {
  if (valor == null || valor === '') return '';
  // Se já parece formato BR (ex: 06/01/2026, 10:36:58), retorna como está
  if (typeof valor === 'string' && /^\d{2}\/\d{2}\/\d{2,4}/.test(valor)) return valor;
  try {
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return String(valor);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return String(valor);
  }
};

// Etiqueta de origem por extenso: a tela é lida por gente que não trabalha com
// as siglas internas (LAB, PCP).
const ORIGEM_LABEL = {
  LAB: 'Laboratório',
  PCP: 'Transporte entre lojas',
  CONFERENCIA: 'Conferência',
  ATENDIMENTO: 'Atendimento na loja'
};

// Mapeamento de ícones para cada tipo de evento da timeline
const getStepIcon = (etapa) => {
  if (etapa.includes('divergencia')) return <DivergenciaIcon />;
  if (etapa.includes('expedido')) return <ShippingIcon />;
  if (etapa.includes('recebido')) return <StoreIcon />;
  // Ícones já importados de propósito: import novo sem conferir o export
  // derruba a página inteira em runtime.
  if (etapa.includes('orcamento')) return <ScheduleIcon />;
  if (etapa.includes('inclusao')) return <InfoIcon />;
  if (etapa.includes('erro')) return <ErrorIcon />;
  if (etapa.includes('producao')) return <LabIcon />;
  return <BusinessIcon />;
};

const LinhaTempo = () => {
  const [codigoRequisicao, setCodigoRequisicao] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dados, setDados] = useState(null); // Estado para armazenar a resposta completa
  const [dialogDetalhes, setDialogDetalhes] = useState(false);
  const [etapaSelecionada, setEtapaSelecionada] = useState(null);
  const [digitoAtivo, setDigitoAtivo] = useState(null); // aba de dígito selecionada

  const { api } = useAuth();

  const buscarLinhaTempo = async () => {
    if (!codigoRequisicao.trim()) {
      setError('Por favor, digite o código da requisição');
      return;
    }

    setLoading(true);
    setError('');
    setDados(null);

    try {
      // A API agora usa o endpoint do searchController
      const response = await api.buscarLinhaTempo(codigoRequisicao.trim());
      
      // ✅ CORREÇÃO: Aceita dados em qualquer formato da API
      const temDados = 
        (response.success && response.data?.timeline_completa?.length > 0) ||
        (response.success && Array.isArray(response.dados) && response.dados.length > 0) ||
        (response.success && Array.isArray(response.timeline) && response.timeline.length > 0);
      
      if (temDados) {
        const dadosParaSalvar = response.dados ? response : (response.data || response);
        setDados(dadosParaSalvar);
        // Abre na aba do dígito pesquisado (se existir entre os disponíveis)
        const disponiveis = dadosParaSalvar?.series_disponiveis || [];
        const pesquisado = dadosParaSalvar?.serie_pesquisada;
        setDigitoAtivo(
          disponiveis.includes(pesquisado) ? pesquisado : (disponiveis[0] ?? null)
        );
      } else {
        setError(response.message || 'Nenhum dado encontrado para esta requisição.');
      }

    } catch (err) {
      console.error('Erro ao buscar linha do tempo:', err);
      setError(err.message || 'Erro ao buscar dados da requisição');
    } finally {
      setLoading(false);
    }
  };



  const mostrarDetalhes = (etapa) => {
    setEtapaSelecionada(etapa);
    setDialogDetalhes(true);
  };
  
  // Suporta ambos formatos: dados (antigo) ou data.timeline_completa (novo)
  let dadosLinhaTempoRaw =
    (Array.isArray(dados?.timeline_completa) && dados?.timeline_completa) ||
    (Array.isArray(dados?.data?.timeline_completa) && dados?.data?.timeline_completa) ||
    (Array.isArray(dados?.dados) && dados?.dados) ||
    (Array.isArray(dados?.timeline) && dados?.timeline) ||
    [];

  // Dígitos (séries) da requisição: uma aba por dígito. Eventos sem série
  // (divergências) valem para a requisição inteira e aparecem em todas as abas.
  const digitosDisponiveis = Array.isArray(dados?.series_disponiveis) ? dados.series_disponiveis : [];
  const temAbas = digitosDisponiveis.length > 1;
  const digitoSelecionado = digitoAtivo ?? digitosDisponiveis[0] ?? null;

  if (temAbas && digitoSelecionado !== null) {
    dadosLinhaTempoRaw = dadosLinhaTempoRaw.filter(
      (e) => e?.serie == null || String(e.serie) === String(digitoSelecionado)
    );
  }

  // PCP da aba ativa (cada dígito tem sua própria expedição/recebimento).
  // Com abas de dígitos que o PCP não conhece, cair no medicacao_pcp mostraria
  // quantidade e filial de OUTRO dígito embaixo desta aba — dado errado.
  const medicacaoPcpAba = temAbas
    ? (dados?.medicacao_pcp_por_serie?.[digitoSelecionado] || null)
    : dados?.medicacao_pcp;

  // Quem cuidou da receita na loja: segue a aba quando há abas.
  const quemCuidou =
    (temAbas && dados?.origem_por_digito?.[digitoSelecionado]) || dados?.origem_resumo || null;

  // Transforma os dados crus em objetos formatados para o Stepper
  const dadosLinhaTempo = dadosLinhaTempoRaw.map((etapa, idx) => ({
    ...etapa, // mantém todos os campos originais
    // Se já está formatado, retorna direto
    ...(etapa.titulo ? etapa : {}),
    // Se não está formatado, cria campos padrão
    titulo: etapa.titulo || `Movimentação - ${etapa.setor || 'N/A'}`,
    descricao: etapa.descricao || etapa.setor || 'Sem descrição',
    etapa: etapa.etapa || 'lab_producao',
    cor: etapa.cor || 'info',
    icone: etapa.icone || '🧪',
    funcionario: etapa.funcionario || 'Funcionário não identificado',
    funcionario_codigo: etapa.codigo_funcionario || etapa.funcionario_codigo || 'N/A',
    setor: etapa.setor || 'N/A',
    origem: etapa.origem || 'LAB',
    data_formatada: formatarDataHoraLocal(etapa.data_formatada || etapa.data_original || etapa.data) || '',
    tempo_desde_anterior: etapa.tempo_desde_anterior || null,
    ordem: idx + 1,
    detalhes: etapa.detalhes || {}
  }));

  // Situação da FÓRMULA da aba, não da requisição inteira: cada dígito anda por
  // conta própria e o status_atual do backend é o último evento de todos juntos
  // (mostrava "Logística" na aba do dígito que nem saiu do laboratório).
  // Como a lista já vem filtrada pela aba e ordenada por data, o último item dela
  // é a situação daquela fórmula.
  const ultimoEventoAba = dadosLinhaTempo.length > 0
    ? dadosLinhaTempo[dadosLinhaTempo.length - 1]
    : null;

  const COR_MUI = { danger: 'error', warning: 'warning', success: 'success', info: 'info' };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <TimelineIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">🔍 Linha do Tempo</Typography>
            <Typography variant="subtitle1" opacity={0.9}>Rastreamento completo da requisição (LAB + PCP)</Typography>
          </Box>
        </Box>
      </Paper>

      <Card elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">📝 Buscar Requisição</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              label="Código da Requisição"
              value={codigoRequisicao}
              onChange={(e) => setCodigoRequisicao(e.target.value)}
              placeholder="Digite o código completo ou parcial..."
              InputProps={{ startAdornment: <ScannerIcon sx={{ mr: 1, color: '#64748b' }} /> }}
              onKeyPress={(e) => { if (e.key === 'Enter') buscarLinhaTempo(); }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={buscarLinhaTempo} disabled={loading} sx={{ height: 56, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {dados && (
        <Card elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">📋 Histórico da Requisição: {dados.medicacao_pcp?.codigo || dados.codigo_info?.original}</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip
                      icon={ultimoEventoAba ? getStepIcon(ultimoEventoAba.etapa) : <CheckCircleIcon />}
                      label={ultimoEventoAba
                        ? `${temAbas ? `Dígito ${digitoSelecionado}: ` : ''}${ultimoEventoAba.titulo}${ultimoEventoAba.data_formatada ? ` — ${ultimoEventoAba.data_formatada}` : ''}`
                        : 'Esta fórmula ainda não teve movimentação'}
                      color={COR_MUI[ultimoEventoAba?.cor] || 'info'}
                      variant="outlined"
                  />
                  <Tooltip title="Atualizar dados">
                    <IconButton onClick={buscarLinhaTempo} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
            </Box>
            
            {/* Abas por dígito (série): cada fórmula da requisição percorre o LAB por conta própria */}
            {temAbas && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={digitoSelecionado}
                  onChange={(e, novo) => setDigitoAtivo(novo)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {digitosDisponiveis.map((d) => (
                    <Tab key={d} value={d} label={`Dígito ${d}`} />
                  ))}
                </Tabs>
              </Box>
            )}

            {/* Quem cuidou da receita na loja — agrupado por PESSOA: em 4 de cada
                10 receitas as três funções são da mesma pessoa, e repetir o nome
                em três colunas se lê como defeito do sistema. */}
            {quemCuidou && (quemCuidou.pessoas?.length > 0 || quemCuidou.orcamento?.ausente) && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  👥 Quem cuidou desta receita na loja{temAbas ? ` — Dígito ${digitoSelecionado}` : ''}
                </Typography>

                {quemCuidou.orcamento?.quando && (
                  <Typography variant="body1">
                    Orçamento feito {quemCuidou.orcamento.quando}
                    {quemCuidou.orcamento.numero ? ` — nº ${quemCuidou.orcamento.numero}` : ''}.
                  </Typography>
                )}
                {quemCuidou.inclusao?.quando && (
                  <Typography variant="body1">
                    Receita digitada {quemCuidou.inclusao.quando}
                    {quemCuidou.inclusao.sem_hora ? ', sem a hora registrada' : ''}.
                  </Typography>
                )}
                {quemCuidou.orcamento?.ausente && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {quemCuidou.orcamento.ausente}
                  </Typography>
                )}

                <Box sx={{ mt: 1.5 }}>
                  {(quemCuidou.pessoas || []).map((p) => (
                    <Typography key={p.nome} variant="body1" sx={{ mb: 0.25 }}>
                      <strong>{p.nome}</strong>{p.texto.replace(p.nome, '')}
                    </Typography>
                  ))}
                  {(quemCuidou.sem_registro || []).map((frase) => (
                    <Typography key={frase} variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                      {frase}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            )}

            {/* Informações da medicação PCP (do dígito ativo) */}
            {medicacaoPcpAba && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="info.dark" gutterBottom>
                  📦 Informações da Medicação (PCP){temAbas ? ` — Dígito ${digitoSelecionado}` : ''}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Código:</Typography>
                    <Typography variant="body2" fontWeight="bold">{medicacaoPcpAba.codigo}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Quantidade:</Typography>
                    <Typography variant="body2" fontWeight="bold">{medicacaoPcpAba.quantidade}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Origem:</Typography>
                    <Typography variant="body2" fontWeight="bold">{medicacaoPcpAba.filial_origem?.nome}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Destino:</Typography>
                    <Typography variant="body2" fontWeight="bold">{medicacaoPcpAba.filial_destino?.nome}</Typography>
                  </Grid>


                </Grid>
              </Paper>
            )}

            <Divider sx={{ mb: 3 }} />

            <Stepper orientation="vertical">
            {dadosLinhaTempo.map((etapa, index) => {
              return (
              <Step key={index} active={true}>
                <StepLabel

                  StepIconComponent={() => (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40,
                      borderRadius: '50%',
                      backgroundColor: etapa.cor === 'danger' ? 'error.main' :
                                       etapa.cor === 'warning' ? 'warning.main' :
                                       etapa.cor === 'success' ? 'success.main' : 'primary.main',
                      color: 'white',
                      boxShadow: 3
                    }}>
                      {getStepIcon(etapa.etapa)}
                    </Box>
                  )}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{etapa.titulo}</Typography>
                      <Typography variant="body2" color="textSecondary">{etapa.data_formatada}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Evento sem dígito (quase toda divergência) vale para a
                          receita inteira e por isso repete em todas as abas. */}
                      {temAbas && etapa.serie == null && (
                        <Chip label="vale para a receita toda" size="small" variant="outlined" />
                      )}
                      <Chip
                        label={ORIGEM_LABEL[etapa.origem] || etapa.origem}
                        size="small"
                        color={etapa.origem === 'PCP' ? 'secondary' : 'info'}
                      />
                    </Box>
                  </Box>
                </StepLabel>
                <StepContent>
                  <Paper elevation={1} sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    borderLeft: `4px solid`,
                    borderColor: etapa.cor === 'danger' ? 'error.main' :
                                 etapa.cor === 'warning' ? 'warning.main' :
                                 etapa.cor === 'success' ? 'success.main' : 'primary.main'
                  }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Funcionário:</strong> {etapa.funcionario}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Setor:</strong> {etapa.setor}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Descrição:</strong> {etapa.descricao}
                      </Typography>

                      {etapa.tempo_desde_anterior && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              <strong>⏱️ Tempo desde a etapa anterior:</strong> {etapa.tempo_desde_anterior}
                          </Typography>
                      )}
                      <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => mostrarDetalhes(etapa)}>
                        Ver Detalhes
                      </Button>
                  </Paper>
                </StepContent>
              </Step>
              );
            })}
          </Stepper>

          {/* Estatísticas da Timeline */}
          {dados.estatisticas && (
            <Paper sx={{ p: 2, mt: 3, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>📊 Estatísticas da Timeline</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Total de Eventos:</Typography>
                  <Typography variant="body2" fontWeight="bold">{dados.estatisticas.total_eventos}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Eventos LAB:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="info.main">{dados.estatisticas.eventos_lab}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Eventos PCP:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="secondary.main">{dados.estatisticas.eventos_pcp}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Período Total:</Typography>
                  <Typography variant="body2" fontWeight="bold">{dados.estatisticas.periodo_total}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Card>
      )}

      <Dialog open={dialogDetalhes} onClose={() => setDialogDetalhes(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center" gap={2}><InfoIcon color="primary" /><Typography variant="h6">Detalhes da Etapa</Typography></Box></DialogTitle>
        <DialogContent>
          {etapaSelecionada && (
            <List>
              <ListItem><ListItemText primary="TÍTULO" secondary={etapaSelecionada.titulo || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="FUNCIONÁRIO" secondary={`${etapaSelecionada.funcionario} (${etapaSelecionada.funcionario_codigo})`} /></ListItem>
              <ListItem><ListItemText primary="SETOR" secondary={etapaSelecionada.setor || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="DATA/HORA" secondary={etapaSelecionada.data_formatada || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="ORIGEM DO DADO" secondary={etapaSelecionada.origem || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="TIPO DE EVENTO" secondary={etapaSelecionada.tipo || 'N/A'} /></ListItem>
              {etapaSelecionada.detalhes?.motivo && (
                <ListItem>
                  <ListItemText primary="MOTIVO DO ERRO" secondary={etapaSelecionada.detalhes.motivo} />
                </ListItem>
              )}
              {etapaSelecionada.detalhes?.filial_destino && (
                <ListItem>
                  <ListItemText primary="FILIAL DESTINO" secondary={etapaSelecionada.detalhes.filial_destino} />
                </ListItem>
              )}
              {etapaSelecionada.detalhes?.cargo_usuario && (
                <ListItem>
                  <ListItemText primary="CARGO DO USUÁRIO" secondary={etapaSelecionada.detalhes.cargo_usuario} />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>


    </Box>
  );
};

export default LinhaTempo;
